"use client";

import { create } from "zustand";

const SIDEBAR_PIN_STORAGE_KEY = "inora_sidebar_pinned";

function readSidebarPinned(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(SIDEBAR_PIN_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

function writeSidebarPinned(pinned: boolean) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(SIDEBAR_PIN_STORAGE_KEY, pinned ? "1" : "0");
  } catch {
    /* ignore */
  }
}

type SidebarPinState = {
  isPinned: boolean;
  hydrated: boolean;
  hydrate: () => void;
  togglePin: () => void;
  setPinned: (pinned: boolean) => void;
};

// Read pinned state synchronously on client so the initial Zustand value
// matches localStorage before the first render. On the server this safely
// returns false (typeof window === "undefined" guard in readSidebarPinned).
// This prevents the SSR(collapsed) → client(expanded) hydration CLS shift.
const INITIAL_PINNED = readSidebarPinned();

export const useSidebarPinStore = create<SidebarPinState>((set, get) => ({
  isPinned: INITIAL_PINNED,
  hydrated: INITIAL_PINNED, // already hydrated if we read a value synchronously
  hydrate: () => {
    const pinned = readSidebarPinned();
    set({ isPinned: pinned, hydrated: true });
  },
  togglePin: () => {
    const next = !get().isPinned;
    writeSidebarPinned(next);
    set({ isPinned: next });
    window.dispatchEvent(
      new CustomEvent("inora-sidebar-pin-change", { detail: { pinned: next } }),
    );
  },
  setPinned: (pinned) => {
    writeSidebarPinned(pinned);
    set({ isPinned: pinned });
    window.dispatchEvent(
      new CustomEvent("inora-sidebar-pin-change", { detail: { pinned } }),
    );
  },
}));

export function useSidebarPin() {
  const isPinned = useSidebarPinStore((s) => s.isPinned);
  const togglePin = useSidebarPinStore((s) => s.togglePin);
  const hydrate = useSidebarPinStore((s) => s.hydrate);
  const hydrated = useSidebarPinStore((s) => s.hydrated);
  return { isPinned, togglePin, hydrate, hydrated };
}
